import LegalPageLayout, {
  LegalH2,
  LegalH3,
  LegalLink,
} from '@/components/LegalPageLayout';

const TermsConditions = () => (
  <LegalPageLayout
    title="Terms and Conditions"
    effectiveDate="06 April 2026"
    lastUpdated="06 April 2026"
    version="1.0"
  >
    {/* ── 1. Introduction and Acceptance ────────────────────────────────── */}
    <LegalH2>1. Introduction and Acceptance</LegalH2>
    <p>
      These Terms and Conditions ("Terms") govern your access to and use of the
      Share Shack application and website (the "Service"), operated by Share
      Shack ("we", "our", "us").
    </p>
    <p style={{ marginTop: 12 }}>
      By creating an account or using the Service, you confirm that you have
      read, understood, and agree to be bound by these Terms and our Privacy
      Policy. If you do not agree, you must not use the Service.
    </p>
    <p style={{ marginTop: 12 }}>
      We may update these Terms from time to time. Where changes are material, we
      will notify you via email or in-app notification at least 14 days before
      the changes take effect. Continued use after notification constitutes
      acceptance. If you disagree, you may delete your account before the changes
      take effect.
    </p>

    {/* ── 2. Definitions ───────────────────────────────────────────────── */}
    <LegalH2>2. Definitions</LegalH2>
    <p>
      <strong>"Service"</strong> means the Share Shack application, website, and
      all related features and functionality.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"User"</strong> means any individual who creates an account on the
      Service.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"Group"</strong> means a private, invite-only collection of Users
      who share visibility of their items and loan activity with each other.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"Owner"</strong> means a User who lists an item within a Group.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"Borrower"</strong> means a User who borrows an item from an Owner.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"Item"</strong> means any physical object listed on the Service by
      a User.
    </p>
    <p style={{ marginTop: 4 }}>
      <strong>"Content"</strong> means text, images, and any other material
      submitted to the Service by Users.
    </p>

    {/* ── 3. Eligibility ───────────────────────────────────────────────── */}
    <LegalH2>3. Eligibility</LegalH2>
    <p>
      You must be at least 13 years old to create an account and use the Service.
    </p>
    <p style={{ marginTop: 12 }}>
      By registering, you represent and warrant that you are at least 13 years of
      age. We rely on the age restrictions enforced by the Apple App Store and
      Google Play Store as our age assurance mechanism.
    </p>
    <p style={{ marginTop: 12 }}>
      If you are between 13 and 17 years old, you confirm that you have your
      parent or guardian's permission to use the Service and that they have read
      and agreed to these Terms on your behalf.
    </p>
    <p style={{ marginTop: 12 }}>
      If we discover or reasonably believe that a User is under 13, we reserve
      the right to immediately suspend or terminate the account and delete all
      associated data without notice.
    </p>

    {/* ── 4. Your Account ──────────────────────────────────────────────── */}
    <LegalH2>4. Your Account</LegalH2>

    <LegalH3>4.1 Registration</LegalH3>
    <p>
      You must provide accurate and complete information during registration and
      keep your account details up to date. You are responsible for all activity
      under your account.
    </p>

    <LegalH3>4.2 Security</LegalH3>
    <p>
      You are responsible for maintaining the confidentiality of your login
      credentials. You must notify us immediately at{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>{' '}
      if you suspect unauthorised access to your account.
    </p>

    <LegalH3>4.3 One Account Per Person</LegalH3>
    <p>
      Each individual may only hold one account. Creating multiple accounts may
      result in all associated accounts being suspended or terminated.
    </p>

    {/* ── 5. How the Service Works ─────────────────────────────────────── */}
    <LegalH2>5. How the Service Works</LegalH2>

    <LegalH3>5.1 What Share Shack Does</LegalH3>
    <p>
      Share Shack is a private item catalogue and loan tracker for trusted
      groups. It allows you to record which items you own, share that catalogue
      with your group members, track who has borrowed what, and manage loan
      status (requests, approvals, due dates, returns).
    </p>
    <p style={{ marginTop: 8 }}>
      Share Shack does not facilitate physical meetups, arrange item delivery or
      collection, provide location data, or enable contact between strangers. All
      interactions occur within private, invite-only groups where members already
      know each other.
    </p>

    <LegalH3>5.2 Platform Role</LegalH3>
    <p>
      Share Shack is a record-keeping tool, not a party to any lending
      arrangement between users.
    </p>
    <p style={{ marginTop: 8 }}>
      We provide the digital infrastructure for tracking items and loans. Any
      physical exchange of items is arranged independently between users outside
      the Service. We do not own, inspect, insure, value, or guarantee any item
      listed on the Service.
    </p>

    <LegalH3>5.3 Groups</LegalH3>
    <p>
      Groups are private and invite-only. Any group member may invite others via
      a time-limited invite link. Groups have a flat structure with no admin
      roles or elevated permissions. The group creator has no special authority
      beyond having created the group.
    </p>

    <LegalH3>5.4 Item Listings</LegalH3>
    <p>
      By listing an item, you represent that:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        You are the legal owner of the item (or have authorisation to include
        it).
      </li>
      <li>The listing description and photos are accurate and not misleading.</li>
      <li>
        You have set an appropriate circulation policy (Return to Owner or Free
        Circulation) for the item.
      </li>
    </ul>

    <LegalH3>5.5 Circulation Policy</LegalH3>
    <p>
      Each item has an owner-defined circulation policy that is enforced by the
      system. "Return to Owner Required" means the item must be returned to the
      owner before it can be lent to someone else. "Free Circulation Allowed"
      means the current holder may lend the item directly to another group member
      without returning it to the owner first. This policy is set by the item
      owner and cannot be overridden by other users.
    </p>

    <LegalH3>5.6 Loans and Returns</LegalH3>
    <p>
      Loan status changes (requests, approvals, returns) are recorded within the
      Service. Loan history is retained for the duration of your account to
      maintain accountability and transparency within the group.
    </p>

    {/* ── 6. Subscriptions and Payments ────────────────────────────────── */}
    <LegalH2>6. Subscriptions and Payments</LegalH2>

    <LegalH3>6.1 Free Tier</LegalH3>
    <p>
      The free tier provides access to the Service with the following limits: 1
      group and 10 items. These limits are enforced by the system.
    </p>

    <LegalH3>6.2 Paid Tier</LegalH3>
    <p>
      The paid tier removes item and group limits, providing unlimited items and
      unlimited groups. The current subscription price is approximately £20 per
      year (annual billing) or £2.50 per month (monthly billing). We reserve the
      right to change pricing with 30 days' notice.
    </p>

    <LegalH3>6.3 Payment Processing</LegalH3>
    <p>
      Payments are processed by Stripe. We do not receive, store, or have access
      to your payment card details. Stripe's own terms and privacy policy apply
      to payment processing.
    </p>

    <LegalH3>6.4 Cancellation and Refunds</LegalH3>
    <p>
      You may cancel your subscription at any time. Your paid features will
      remain active until the end of your current billing period. After
      cancellation, your account reverts to the free tier and tier limits are
      re-applied.
    </p>
    <p style={{ marginTop: 8 }}>
      Refunds are handled on a case-by-case basis. Contact{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>{' '}
      for refund requests. We will comply with your statutory rights under the
      Consumer Rights Act 2015 and the Consumer Contracts Regulations 2013.
    </p>

    {/* ── 7. User Conduct and Prohibited Activities ────────────────────── */}
    <LegalH2>7. User Conduct and Prohibited Activities</LegalH2>
    <p>You agree not to:</p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        List items you do not own or are not authorised to include.
      </li>
      <li>
        Provide false or misleading information in listings or your profile.
      </li>
      <li>
        Use the Service for commercial purposes (Share Shack is for personal,
        non-commercial item sharing).
      </li>
      <li>
        Attempt to circumvent subscription tier limits or age restrictions.
      </li>
      <li>
        Use automated tools, bots, or scripts to access the Service.
      </li>
      <li>Impersonate another person or misrepresent your identity.</li>
      <li>Harass, threaten, or abuse other users.</li>
      <li>Violate any applicable law or regulation.</li>
    </ul>
    <p>
      Violation of these terms may result in warnings, temporary suspension, or
      permanent account termination.
    </p>

    {/* ── 8. User Content ──────────────────────────────────────────────── */}
    <LegalH2>8. User Content</LegalH2>

    <LegalH3>8.1 Ownership</LegalH3>
    <p>
      You retain ownership of content you submit (item photos, descriptions). By
      submitting content, you grant us a non-exclusive, royalty-free licence to
      use, display, and distribute that content solely for the purpose of
      operating the Service.
    </p>

    <LegalH3>8.2 Responsibility</LegalH3>
    <p>
      You are solely responsible for content you submit. Content must be
      accurate, lawful, and must not infringe the intellectual property rights of
      any third party.
    </p>

    <LegalH3>8.3 Removal</LegalH3>
    <p>
      We reserve the right to remove any content that violates these Terms or
      that we reasonably believe is harmful, misleading, or unlawful.
    </p>

    {/* ── 9. Liability and Disclaimers ─────────────────────────────────── */}
    <LegalH2>9. Liability and Disclaimers</LegalH2>

    <LegalH3>9.1 Platform Disclaimer</LegalH3>
    <p>
      The Service is provided "as is" and "as available". To the fullest extent
      permitted by law, we make no warranties regarding the Service, the items
      listed, or the conduct of users.
    </p>
    <p style={{ marginTop: 8 }}>
      Share Shack is a record-keeping tool. We are not responsible for the
      physical condition, safety, legality, or fitness for purpose of any item
      listed on the Service, or for any interaction between users that takes
      place outside the Service.
    </p>

    <LegalH3>9.2 Limitation of Liability</LegalH3>
    <p>
      To the maximum extent permitted by applicable law:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        We are not liable for any loss, damage, or dispute arising from any
        lending arrangement between users.
      </li>
      <li>
        We are not liable for the loss, damage, or theft of any item, whether or
        not the loan was recorded via the Service.
      </li>
      <li>
        We are not liable for any indirect, incidental, special, consequential,
        or punitive damages.
      </li>
      <li>
        Our total aggregate liability to you for any claims arising from or
        relating to the Service shall not exceed the amount you have paid to us
        in the 12 months preceding the claim, or £50, whichever is greater.
      </li>
    </ul>
    <p>
      Nothing in these Terms excludes or limits our liability for death or
      personal injury caused by our negligence, fraud or fraudulent
      misrepresentation, or any liability that cannot be excluded or limited
      under English law (including your statutory rights under the Consumer
      Rights Act 2015).
    </p>

    <LegalH3>9.3 No Guarantee of Item Safety or Quality</LegalH3>
    <p>
      Share Shack does not inspect, verify, or endorse any item listed on the
      Service. Responsibility for the condition and safety of items rests
      entirely with the owner. If you borrow an item, you do so at your own risk.
    </p>

    {/* ── 10. Disputes Between Users ───────────────────────────────────── */}
    <LegalH2>10. Disputes Between Users</LegalH2>
    <p>
      Disputes between users regarding items (e.g. damage, late return,
      non-return) are between those users directly. Share Shack is a
      record-keeping tool and does not mediate, arbitrate, or adjudicate disputes
      between users.
    </p>
    <p style={{ marginTop: 8 }}>
      The loan history recorded in the Service may assist users in resolving
      disputes, but we make no guarantee as to its completeness or admissibility
      in any legal proceedings.
    </p>

    {/* ── 11. Intellectual Property ────────────────────────────────────── */}
    <LegalH2>11. Intellectual Property</LegalH2>
    <p>
      All intellectual property rights in the Service (including design, branding,
      code, and documentation) belong to us or our licensors. Nothing in these
      Terms grants you any rights in our intellectual property except the limited
      right to use the Service in accordance with these Terms.
    </p>

    {/* ── 12. Suspension and Termination ───────────────────────────────── */}
    <LegalH2>12. Suspension and Termination</LegalH2>

    <LegalH3>12.1 By You</LegalH3>
    <p>
      You may delete your account at any time through your Account settings. Upon
      deletion, your personal data will be handled in accordance with our Privacy
      Policy (30-day soft-delete, then permanent erasure). You must return any
      borrowed items before deleting your account.
    </p>

    <LegalH3>12.2 By Us</LegalH3>
    <p>
      We may suspend or terminate your account at any time if you breach these
      Terms, if we reasonably believe you pose a risk to other users, if required
      by law, or if the Service is discontinued. Where reasonably practicable, we
      will notify you and provide reasons.
    </p>

    {/* ── 13. Users Aged 13–17 ─────────────────────────────────────────── */}
    <LegalH2>13. Users Aged 13–17</LegalH2>
    <p>
      Share Shack is designed for families and trusted groups. We welcome users
      aged 13–17 and take their privacy seriously.
    </p>
    <p style={{ marginTop: 12 }}>
      Users aged 13–17 may use the Service with parent or guardian consent. The
      Service is designed with privacy and safety in mind for all age groups, in
      conformance with the ICO's Age Appropriate Design Code (Children's Code).
    </p>
    <p style={{ marginTop: 12 }}>
      Key protections for younger users include:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        All groups are private and invite-only (no public profiles or
        discovery).
      </li>
      <li>
        No contact with strangers is possible through the Service.
      </li>
      <li>No location data is collected or shared.</li>
      <li>
        No profiling, behavioural advertising, or nudge techniques are used.
      </li>
      <li>High-privacy settings are applied by default for all users.</li>
      <li>
        Data collection is limited to the minimum necessary (display name,
        email, items, and loans).
      </li>
    </ul>
    <p>
      Parents and guardians can contact us at{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>{' '}
      with any questions or concerns about their child's use of the Service.
    </p>
    <p style={{ marginTop: 8 }}>
      If we discover a user is under 13, we will delete their account and all
      associated data immediately.
    </p>

    {/* ── 14. Privacy and Data Protection ──────────────────────────────── */}
    <LegalH2>14. Privacy and Data Protection</LegalH2>
    <p>
      Your use of the Service is also governed by our Privacy Policy, which forms
      part of these Terms. Please read it carefully. The Privacy Policy is
      accessible within the app and at{' '}
      <LegalLink href="https://shareshack.co.uk/privacy">
        https://shareshack.co.uk/privacy
      </LegalLink>
      .
    </p>

    {/* ── 15. Governing Law and Jurisdiction ────────────────────────────── */}
    <LegalH2>15. Governing Law and Jurisdiction</LegalH2>
    <p>
      These Terms are governed by the laws of England and Wales. Any disputes
      arising from or in connection with these Terms shall be subject to the
      exclusive jurisdiction of the courts of England and Wales.
    </p>
    <p style={{ marginTop: 8 }}>
      If you are a consumer, you will benefit from any mandatory provisions of
      the law of the country in which you are resident. Nothing in these Terms
      affects your statutory rights as a consumer.
    </p>

    {/* ── 16. General Provisions ───────────────────────────────────────── */}
    <LegalH2>16. General Provisions</LegalH2>
    <p>
      <strong>Entire Agreement:</strong> These Terms, together with the Privacy
      Policy, constitute the entire agreement between you and Share Shack
      regarding your use of the Service.
    </p>
    <p style={{ marginTop: 8 }}>
      <strong>Severability:</strong> If any provision of these Terms is found to
      be invalid or unenforceable, the remaining provisions will continue in full
      force and effect.
    </p>
    <p style={{ marginTop: 8 }}>
      <strong>Waiver:</strong> Our failure to enforce any right or provision of
      these Terms shall not constitute a waiver of that right or provision.
    </p>
    <p style={{ marginTop: 8 }}>
      <strong>Assignment:</strong> We may assign or transfer our rights and
      obligations under these Terms. You may not assign or transfer yours without
      our prior written consent.
    </p>
    <p style={{ marginTop: 8 }}>
      <strong>Third-Party Rights:</strong> These Terms do not confer any rights
      on any person other than the parties under the Contracts (Rights of Third
      Parties) Act 1999.
    </p>

    {/* ── 17. Contact Us ───────────────────────────────────────────────── */}
    <LegalH2>17. Contact Us</LegalH2>
    <p>For questions about these Terms:</p>
    <p style={{ marginTop: 8 }}>
      Email:{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>
      <br />
      Share Shack
    </p>
  </LegalPageLayout>
);

export default TermsConditions;
